import {
  AssetTagSchema,
  AuthenticationSchema,
  EndpointExpectedResponseSchema,
  EndpointExpectedResponseVerbSchema,
  EndpointSchema,
  EndpointSourceSchema,
  EndpointWebSocketSchema,
  EndpointGrpcSchema,
  FindingSeveritySchema,
  HttpMethodSchema,
  HttpRequestWithRelationsSchema,
  HttpResponseWithRelationsSchema,
  ProtocolAssetTagSchema,
  SignalOriginSchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import {
  HarEntrySchema,
  WebGrpcDecodedDataSchema,
} from '@terra/shared-schemas-tracker';
import { z } from 'zod';
import {
  AssetLifecycleDtoSchema,
  LifecycleStateFilterSchema,
} from '@terra/shared-dto-types';
import { ENDPOINT_DETECTIONS_MAX_ITEMS } from './endpoints.constants';

export { EndpointExpectedResponseSchema, EndpointExpectedResponseVerbSchema };

export const EndpointSecurityTypeSchema = z.enum([
  'vulnerable',
  'sensitive',
  'clear',
]);

export type EndpointSecurityType = z.infer<typeof EndpointSecurityTypeSchema>;

export const ENDPOINT_SECURITY_TYPE = {
  VULNERABLE: 'vulnerable' as const,
  SENSITIVE: 'sensitive' as const,
  CLEAR: 'clear' as const,
} as const;

export type EndpointExpectedResponseVerb = z.infer<
  typeof EndpointExpectedResponseVerbSchema
>;

export const EndpointsParamsSchema = z.object({
  applicationId: z.string().describe('The application id'),
});

export const EndpointsListQuerySchema = z.object({
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
});

export const EndpointsPreviewQuerySchema = z.object({
  url: z
    .string()
    .optional()
    .describe('Filter endpoints by URL (case-insensitive partial match)'),
  method: HttpMethodSchema.optional().describe(
    'Filter endpoints by HTTP method (GET, POST, PUT, DELETE, PATCH)',
  ),
  createdStartDate: z.coerce
    .date()
    .optional()
    .describe('Filter endpoints created on or after this date'),
  createdEndDate: z.coerce
    .date()
    .optional()
    .describe('Filter endpoints created on or before this date'),
  updatedStartDate: z.coerce
    .date()
    .optional()
    .describe('Filter endpoints updated on or after this date'),
  updatedEndDate: z.coerce
    .date()
    .optional()
    .describe('Filter endpoints updated on or before this date'),
  isPubliclyAccessible: z
    .boolean()
    .optional()
    .describe('Filter by public accessibility status'),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .describe('Maximum number of endpoints to return'),
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of endpoints to skip (for pagination)'),
  metrics: z.coerce
    .boolean()
    .optional()
    .describe(
      'Include aggregated metrics (findings counts by severity, signals count) for endpoints',
    ),
  crawlerSessionId: z
    .string()
    .regex(/^[a-f\d]{24}$/i, 'Invalid crawler session id')
    .optional()
    .describe(
      'When set, return only endpoints newly discovered in this crawler session',
    ),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
});

const BaseEndpointCreateSchema = EndpointSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  tenantId: true,
}).extend({
  websocket: EndpointWebSocketSchema.nullable().optional(),
  grpc: EndpointGrpcSchema.nullable().optional(),
});

export const EndpointReturnSchema = BaseEndpointCreateSchema.extend({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  hash: z.string(),
  tenantId: z.string().nullable(),
  signalCheckCompletedByOrigin: SignalOriginSchema.array(),
  request: HttpRequestWithRelationsSchema,
  responses: z.array(HttpResponseWithRelationsSchema),
  example: HttpRequestWithRelationsSchema.nullable().optional(),
  authentication: AuthenticationSchema.nullable().optional(),
  websocket: EndpointWebSocketSchema.nullable().optional(),
  grpc: EndpointGrpcSchema.nullable().optional(),
  codeGraphId: z.string().nullable(),
  expectedResponses: z.array(EndpointExpectedResponseSchema).optional(),
  graphCheckFailed: z.boolean().nullable().optional(),
  graphCheckFailedReason: z.string().nullable().optional(),
  harEntryId: z.string().nullable(),
  webpageIds: z.array(z.string()).optional().default([]),
  credentialRoles: z.array(z.string()).optional(),
  isPubliclyAccessible: z.boolean().nullable().optional(),
  sources: EndpointSourceSchema.array(),
  graphqlOperationName: z.string().nullable().optional(),
  soapOperationName: z.string().nullable().optional(),
  xmlOperationName: z.string().nullable().optional(),
  xmlOperationDisplayName: z.string().nullable().optional(),
  mcpOperationName: z.string().nullable().optional(),
  grpcServiceName: z.string().nullable().optional(),
  grpcMethodName: z.string().nullable().optional(),
  assetTags: AssetTagSchema.array().optional(),
  aiFlowGraphId: z.string().nullable().optional(),
  lifecycle: AssetLifecycleDtoSchema.optional(),
});

export const EndpointsReturnSchema = z.object({
  endpoints: z.array(EndpointReturnSchema).describe('The endpoints'),
});

export const EndpointPreviewSchema = z.object({
  id: z.string(),
  description: z.string().nullable(),
  path: z.string(),
  host: z.string(),
  port: z.int().nullable(),
  url: z.string(),
  request: z.object({
    method: z.enum(['GET', 'POST', 'DELETE', 'PUT', 'PATCH']),
    pathParams: z.array(z.string()).optional(),
    bodySchema: z.string().nullable().optional(),
  }),
  graphqlOperationName: z.string().nullable().optional(),
  soapOperationName: z.string().nullable().optional(),
  xmlOperationName: z.string().nullable().optional(),
  xmlOperationDisplayName: z.string().nullable().optional(),
  mcpOperationName: z.string().nullable().optional(),
  grpcServiceName: z.string().nullable().optional(),
  grpcMethodName: z.string().nullable().optional(),
  grpc: EndpointGrpcSchema.nullable().optional(),
  websocket: EndpointWebSocketSchema.nullable().optional(),
  codeGraphId: z.string().nullable(),
  expectedResponses: z.array(EndpointExpectedResponseSchema).optional(),
  graphCheckFailed: z.boolean().nullable().optional(),
  graphCheckFailedReason: z.string().nullable().optional(),
  isOutdated: z.boolean().nullable().optional(),
  webpageIds: z.array(z.string()).optional().default([]),
  credentialRoles: z.array(z.string()).optional(),
  isPubliclyAccessible: z.boolean().nullable().optional(),
  sources: EndpointSourceSchema.array(),
  assetTags: AssetTagSchema.array().optional(),
  aiFlowGraphId: z.string().nullable().optional(),
  assetType: ProtocolAssetTagSchema.nullable().optional(),
  signalCheckCompletedByOrigin: SignalOriginSchema.array().optional(),
  lifecycle: AssetLifecycleDtoSchema.optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  metrics: z
    .object({
      type: EndpointSecurityTypeSchema.optional().describe(
        'Endpoint security status: vulnerable (has findings), sensitive (has blocked signals), or clear (no findings or signals)',
      ),
      signalsCount: z.int().nonnegative().optional(),
      activeSignalsCount: z.int().nonnegative().optional(),
      inactiveSignalsCount: z.int().nonnegative().optional(),
      allFindingsCount: z.int().nonnegative().optional(),
      findingsCounts: z
        .object({
          critical: z.int().nonnegative().optional(),
          high: z.int().nonnegative().optional(),
          medium: z.int().nonnegative().optional(),
          low: z.int().nonnegative().optional(),
        })
        .optional(),
      activeFindingsCount: z.int().nonnegative().optional(),
      inactiveFindingsCount: z.int().nonnegative().optional(),
    })
    .optional(),
});

export const EndpointsPreviewReturnSchema = z.object({
  endpoints: z.array(EndpointPreviewSchema).describe('The endpoints preview'),
  totalCount: z
    .int()
    .nonnegative()
    .optional()
    .describe('Total number of endpoints matching the filter'),
});

export type EndpointPreviewReturnDto = z.infer<typeof EndpointPreviewSchema>;
export type EndpointsPreviewReturnDto = z.infer<
  typeof EndpointsPreviewReturnSchema
>;

export const CreateEndpointSchema = BaseEndpointCreateSchema.extend({
  request: HttpRequestWithRelationsSchema,
  responses: z.array(HttpResponseWithRelationsSchema),
  example: HttpRequestWithRelationsSchema.nullable().optional(),
  authentication: AuthenticationSchema.nullable().optional(),
  websocket: EndpointWebSocketSchema.nullable().optional(),
  grpc: EndpointGrpcSchema.nullable().optional(),
  codeGraphId: z.string().nullable().optional(),
  expectedResponses: z.array(EndpointExpectedResponseSchema).optional(),
  graphCheckFailed: z.boolean().nullable().optional(),
  graphCheckFailedReason: z.string().nullable().optional(),
  isOutdated: z.boolean().nullable().optional(),
  graphqlOperationName: z.string().nullable().optional(),
  soapOperationName: z.string().nullable().optional(),
  xmlOperationName: z.string().nullable().optional(),
  xmlOperationDisplayName: z.string().nullable().optional(),
  mcpOperationName: z.string().nullable().optional(),
  grpcServiceName: z.string().nullable().optional(),
  grpcMethodName: z.string().nullable().optional(),
  isPubliclyAccessible: z.boolean().nullable().optional(),
  sources: EndpointSourceSchema.array().optional().default(['MANUAL']),
  webpageIds: z.array(z.string()).optional().default([]),
  lifecycle: AssetLifecycleDtoSchema.optional(),
});

export const UpdateEndpointSchema = BaseEndpointCreateSchema.partial().extend({
  request: HttpRequestWithRelationsSchema.optional(),
  responses: z.array(HttpResponseWithRelationsSchema).optional(),
  example: HttpRequestWithRelationsSchema.nullable().optional(),
  authentication: AuthenticationSchema.nullable().optional(),
  websocket: EndpointWebSocketSchema.nullable().optional(),
  grpc: EndpointGrpcSchema.nullable().optional(),
  codeGraphId: z.string().nullable().optional(),
  expectedResponses: z.array(EndpointExpectedResponseSchema).optional(),
  aiFlowGraphId: z.string().nullable().optional(),
  graphCheckFailed: z.boolean().nullable().optional(),
  graphCheckFailedReason: z.string().nullable().optional(),
  isOutdated: z.boolean().nullable().optional(),
  graphqlOperationName: z.string().nullable().optional(),
  soapOperationName: z.string().nullable().optional(),
  xmlOperationName: z.string().nullable().optional(),
  xmlOperationDisplayName: z.string().nullable().optional(),
  mcpOperationName: z.string().nullable().optional(),
  grpcServiceName: z.string().nullable().optional(),
  grpcMethodName: z.string().nullable().optional(),
  sources: EndpointSourceSchema.array().optional(),
  webpageIds: z.array(z.string()).optional(),
});

export const EndpointIdParamsSchema = z.object({
  id: z.string().describe('The endpoint id'),
});

export const DeepActiveProbeResearchParamsSchema = EndpointsParamsSchema.extend(
  {
    endpointId: z.string().describe('The endpoint id'),
    harEntryId: z
      .string()
      .describe('The HAR entry Deep active probe should research'),
  },
);

export const TriggerDeepActiveProbeResearchResponseSchema = z.object({
  message: z.string(),
});

export const EndpointRelatedWebpageSchema = z.object({
  id: z.string(),
  url: z.string(),
  description: z.string().nullable().optional(),
  role: z.string().optional(),
  credentialsId: z.string().nullable().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export const EndpointWebpagesQuerySchema = z.object({
  skip: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Number of related webpages to skip for pagination'),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .describe('Maximum number of related webpages to return'),
});

export const EndpointWebpagesReturnSchema = z.object({
  webpages: z.array(EndpointRelatedWebpageSchema),
  totalCount: z
    .number()
    .int()
    .nonnegative()
    .describe('Total number of webpages related to this endpoint'),
});

export const ErrorResponseSchema = z.object({
  message: z.string(),
});

export const DeleteResponseSchema = z.object({
  message: z.string(),
});

export const GenerateEndpointGraphResponseSchema = z.object({
  message: z.string(),
});

export const TriggerEndpointSignalsGenerationResponseSchema = z.object({
  message: z.string(),
});

export const HarEntriesResponseSchema = z.object({
  harEntries: z.array(HarEntrySchema).describe('The HAR entries'),
  totalCount: z.number().describe('The total count of HAR entries'),
});

export const HarEntryByIdParamsSchema = EndpointsParamsSchema.extend({
  harEntryId: z.string().describe('The HAR entry id'),
});

export const HarEntryResponseSchema = z.object({
  harEntry: HarEntrySchema.extend({
    webGrpcDecodedData: WebGrpcDecodedDataSchema.nullable().optional(),
  }).describe('The HAR entry'),
});

export const EndpointExpectedResponseMatchResultSchema = z.enum([
  'full',
  'status_only',
  'unmatched',
]);

export const EndpointExpectedResponseObservationSchema = z.object({
  id: z.string(),
  applicationId: z.string(),
  endpointId: z.string(),
  signalId: z.string().nullable(),
  threadId: z.string(),
  executionId: z.string(),
  toolCallId: z.string(),
  toolExecutionHarId: z.string(),
  observedStatus: z.int(),
  matchResult: EndpointExpectedResponseMatchResultSchema,
  matchedExpectedResponse: EndpointExpectedResponseSchema.nullable(),
  recordedAt: z.coerce.date(),
});

export const ExpectedResponseObservationsSummarySchema = z.object({
  full: z.int().nonnegative(),
  statusOnly: z.int().nonnegative(),
  unmatched: z.int().nonnegative(),
  endpointCount: z.int().nonnegative(),
  nonStateChangingCount: z.int().nonnegative(),
});

export const EndpointExpectedResponseObservationsResponseSchema = z.object({
  observations: z.array(EndpointExpectedResponseObservationSchema),
  totalCount: z.int().nonnegative(),
  summary: ExpectedResponseObservationsSummarySchema,
});

const optionalBooleanQueryParam = z.preprocess((value) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return value;
}, z.boolean().optional());

export const ExpectedResponseObservationsQuerySchema = z.object({
  skip: z.coerce.number().int().nonnegative().optional(),
  limit: z.coerce.number().int().positive().max(500).optional(),
  endpointIds: z.array(z.string()).optional(),
  expectedVerbs: z.array(EndpointExpectedResponseVerbSchema).optional(),
  expectedEntities: z.array(z.string()).optional(),
  expectedDescriptions: z.array(z.string()).optional(),
  isStateChanging: optionalBooleanQueryParam,
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
});

export const ExpectedResponseObservationFilterOptionsSchema = z.object({
  endpointIds: z.array(z.string()),
  expectedVerbs: z.array(EndpointExpectedResponseVerbSchema),
  expectedEntities: z.array(z.string()),
  expectedDescriptions: z.array(z.string()),
});

export const NeoRunThreadParamsSchema = z.object({
  applicationId: z.string(),
  threadId: z.string(),
});

export const MigrateEndpointsToVectorStoreQuerySchema = z.object({
  applicationId: z
    .string()
    .describe('The application ID to migrate endpoints for'),
  batchSize: z.coerce
    .number()
    .int()
    .positive()
    .max(1000)
    .optional()
    .prefault(100)
    .describe('Number of endpoints to process per batch'),
});

export const MigrateEndpointsToVectorStoreResponseSchema = z.object({
  totalCount: z
    .int()
    .nonnegative()
    .describe('Total number of endpoints in database'),
  totalProcessed: z
    .int()
    .nonnegative()
    .describe('Total number of endpoints processed'),
  totalBatches: z
    .int()
    .nonnegative()
    .describe('Total number of batches processed'),
  successCount: z
    .int()
    .nonnegative()
    .describe('Number of endpoints successfully migrated'),
  errorCount: z
    .int()
    .nonnegative()
    .describe('Number of endpoints that failed to migrate'),
  message: z
    .string()
    .describe('Human-readable message about the migration result'),
});

export type HarEntriesResponseDto = z.infer<typeof HarEntriesResponseSchema>;
export type HarEntryByIdParamsDto = z.infer<typeof HarEntryByIdParamsSchema>;
export type HarEntryResponseDto = z.infer<typeof HarEntryResponseSchema>;
export type MigrateEndpointsToVectorStoreResponseDto = z.infer<
  typeof MigrateEndpointsToVectorStoreResponseSchema
>;

export const EndpointDetectionFindingSchema = z.object({
  id: z.string(),
  title: z.string(),
  severity: FindingSeveritySchema,
  isActive: z.boolean(),
});

export const EndpointDetectionSignalSchema = z.object({
  id: z.string(),
  title: z.string(),
  stage: SignalStageSchema,
  status: SignalStatusSchema.nullable(),
  isActive: z.boolean(),
});

export const EndpointDetectionsReturnSchema = z.object({
  findings: z
    .array(EndpointDetectionFindingSchema)
    .max(ENDPOINT_DETECTIONS_MAX_ITEMS)
    .describe(
      `Findings linked to the endpoint, newest first. At most ${ENDPOINT_DETECTIONS_MAX_ITEMS} items.`,
    ),
  signals: z
    .array(EndpointDetectionSignalSchema)
    .max(ENDPOINT_DETECTIONS_MAX_ITEMS)
    .describe(
      `Signals linked to the endpoint, newest first. At most ${ENDPOINT_DETECTIONS_MAX_ITEMS} items.`,
    ),
});

export type EndpointDetectionFindingDto = z.infer<
  typeof EndpointDetectionFindingSchema
>;
export type EndpointDetectionSignalDto = z.infer<
  typeof EndpointDetectionSignalSchema
>;
export type EndpointDetectionsReturnDto = z.infer<
  typeof EndpointDetectionsReturnSchema
>;
